import { useMemo } from 'react';

import orderBy from 'lodash/orderBy';

import {
  Alert,
  ExternalLink,
  LabelChipV2,
  StatusChipV2,
  Typography,
  Stack,
} from '@cast/design-system';
import { NodeSelectionTemplateSummary } from '@cast/types';

import { CloseableAlert } from 'components/alerts';
import { Detail } from 'components/drawer';
import { makeSelectionTemplatesLink } from 'features/cluster/autoscaler/utils';
import { useSelectionTemplatesQuery } from 'hooks/queries/autoscaler';

import { GoToNodeTemplatesButton } from '../../_components/GoToNodeTemplatesButton';
import { EdgeLocationMappedState, EnhancedEdgeLocation } from '../../types';

type EdgeLocationUsageSectionProps = {
  edgeLocation: EnhancedEdgeLocation;
  clusterId: string;
};

type EdgeLocationTemplateUsageProps = Omit<
  EdgeLocationUsageSectionProps,
  'edgeLocation'
> & {
  linkedTemplates: NodeSelectionTemplateSummary[];
};

const EdgeLocationDisabledTemplatesWarning = ({
  edgeLocation,
  clusterId,
}: EdgeLocationUsageSectionProps) => {
  const { templateUsage, mappedState } = edgeLocation;
  const disabledTemplateCount =
    templateUsage.totalUsed - templateUsage.totalEnabled;
  const allTemplatesDisabled =
    disabledTemplateCount === templateUsage.totalUsed;

  if (
    disabledTemplateCount === 0 ||
    mappedState !== EdgeLocationMappedState.InUse
  ) {
    return null;
  }

  return (
    <CloseableAlert
      color="warning"
      size="small"
      testId={
        allTemplatesDisabled
          ? 'edge-location-disabled-all-templates-alert'
          : 'edge-location-disabled-some-templates-alert'
      }
      closeable="edge-location-disabled-templates-alert"
    >
      <Stack gap={12}>
        {allTemplatesDisabled
          ? 'The Autoscaler cannot use this edge location because its linked node templates are disabled. Enable at least one linked template to use this location.'
          : 'Some node templates for this edge location are disabled. Nodes can only be created from active templates.'}
        <GoToNodeTemplatesButton
          clusterId={clusterId}
          variant="primary"
          size="sm"
        />
      </Stack>
    </CloseableAlert>
  );
};

const EdgeLocationIncompleteSetupWarning = ({
  edgeLocation,
  clusterId,
}: EdgeLocationUsageSectionProps) => {
  const { mappedState, templateUsage } = edgeLocation;

  if (
    !(
      mappedState === EdgeLocationMappedState.Ready &&
      templateUsage.totalUsed === 0
    )
  ) {
    return null;
  }

  return (
    <Alert color="warning" size="sm" testId="edge-location-not-linked-alert">
      This edge location won&apos;t be used by the Autoscaler until it&apos;s
      linked to a node template.
      <GoToNodeTemplatesButton
        clusterId={clusterId}
        variant="primary"
        size="sm"
      />
    </Alert>
  );
};

const EdgeLocationNodeTemplateUsage = ({
  clusterId,
  linkedTemplates,
}: EdgeLocationTemplateUsageProps) => {
  return (
    <Stack gap={8} data-testid="edge-location-node-templates-list">
      {linkedTemplates.map(({ template }) => (
        <Stack
          key={template?.name}
          data-testid="edge-location-node-template-item"
          direction="row"
          gap={8}
          alignItems="center"
          justifyContent="flex-end"
        >
          <ExternalLink
            sx={{ typography: 'P14R' }}
            link={makeSelectionTemplatesLink({
              clusterId,
              openTemplate: template?.name,
            })}
          >
            {template?.name}
          </ExternalLink>
          {template?.isEnabled ? (
            <LabelChipV2 color="blue" label={`V${template.version}`} />
          ) : (
            <StatusChipV2 color="neutral" size="small" label="Disabled" />
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export const EdgeLocationUsageSection = ({
  edgeLocation,
  clusterId,
}: EdgeLocationUsageSectionProps) => {
  const { selectionTemplates } = useSelectionTemplatesQuery({ clusterId });
  const isEdgeLocationInUse =
    edgeLocation.mappedState === EdgeLocationMappedState.InUse;

  const linkedTemplates = useMemo(() => {
    if (!selectionTemplates) {
      return [];
    }

    const usedTemplates = selectionTemplates.filter(({ template }) =>
      template?.edgeLocationConfigs?.some(
        (edgeLocationConfig) =>
          edgeLocationConfig.edgeLocationId === edgeLocation.id
      )
    );

    return orderBy(usedTemplates, 'template.name');
  }, [edgeLocation.id, selectionTemplates]);

  return (
    <Stack gap={12} data-testid="edge-location-usage-section">
      <Typography variant="L12B">Usage</Typography>
      <EdgeLocationIncompleteSetupWarning
        edgeLocation={edgeLocation}
        clusterId={clusterId}
      />
      <EdgeLocationDisabledTemplatesWarning
        edgeLocation={edgeLocation}
        clusterId={clusterId}
      />

      {isEdgeLocationInUse && (
        <Detail
          sx={
            linkedTemplates.length > 1
              ? { alignItems: 'flex-start' }
              : undefined
          }
          title="Node templates"
          detail={
            <EdgeLocationNodeTemplateUsage
              clusterId={clusterId}
              linkedTemplates={linkedTemplates}
            />
          }
        />
      )}
    </Stack>
  );
};
